/**
 * The sorted casefile: P1's output, and P2's input.
 *
 * It lives in the shared package rather than inside the sorter because the
 * Schedule of Documents in a report is the same list, rendered differently.
 * Today someone retypes it. Modelling it once means the sorter's classification
 * becomes the report's schedule with no re-keying and no chance of the two
 * disagreeing.
 *
 * The section list and the grouping rules are Beam's, taken from the existing
 * pipeline's conventions.
 */
import { formatAuDate } from './au/dates.js';
export const SECTION_ORDER = [
    'Referral Letter',
    'Specialist',
    'Imaging',
    // Two sections, not one. Physical treatment and vocational services are read
    // by different people for different purposes and Beam files them separately.
    'Rehabilitation - Physical',
    'Rehabilitation - Vocational',
    'Clinical Notes',
    'Claim Form',
    "Workers' Compensation Certificate",
    'Other',
    'Duplicates',
];
/** Documents grouped under a per-provider parent, reverse chronological within each. */
export const PROVIDER_GROUPED = new Set([
    'Specialist',
    'Rehabilitation - Physical',
    'Rehabilitation - Vocational',
    'Clinical Notes',
]);
/** Grouped by year, descending, reverse chronological within each year. */
export const YEAR_GROUPED = new Set(["Workers' Compensation Certificate"]);
/**
 * Document types that decide the rehabilitation split without a model call.
 *
 * These are here rather than in a prompt because the prompt did not work: the
 * model returned "Rehabilitation - Physical" for a document whose own type it
 * had filled in as "Workplace Rehabilitation Assessment Report", at high
 * confidence. The distinction these draw is lexical and has no judgement in it.
 *
 * "Functional capacity evaluation" is deliberately absent: a treating
 * physiotherapist may run one as part of physical reconditioning, so it stays a
 * judgement call.
 */
export const VOCATIONAL_MARKERS = [
    'workplace rehabilitation',
    'workplace assessment',
    'worksite assessment',
    'vocational',
    'transferable skills',
    'return to work',
    'return-to-work',
    'injury management plan',
    'job analysis',
    'job seeking',
    'job search',
    'suitable duties',
];
export const DATE_TO_CHECK = '( DATE TO CHECK )';
export function isVocational(doc) {
    const haystack = `${doc.docType ?? ''} ${doc.title ?? ''}`.toLowerCase();
    return VOCATIONAL_MARKERS.some((m) => haystack.includes(m));
}
/**
 * Force the vocational side of the rehabilitation split where the document type
 * says so. Only ever moves a document between the two rehabilitation sections,
 * so a mis-set marker costs a shelf, not a document.
 */
export function correctRehabilitationSplit(doc) {
    if (doc.section !== 'Rehabilitation - Physical' && doc.section !== 'Rehabilitation - Vocational')
        return doc;
    if (isVocational(doc))
        return { ...doc, section: 'Rehabilitation - Vocational' };
    return doc;
}
export function formatDocDate(date) {
    if (!date)
        return DATE_TO_CHECK;
    return formatAuDate(date.day, date.month, date.year);
}
/**
 * Title conventions:
 *   Practitioner  Name - Specialty[ - Doc type] - D Month YYYY
 *   Imaging       Modality Body Part - D Month YYYY
 *   WorkCover     WorkCover WA - Certificate type - D Month YYYY
 */
export function buildTitle(parts) {
    const bits = [parts.provider, parts.specialty, parts.docType]
        .map((b) => (b ?? '').trim())
        .filter(Boolean);
    bits.push(formatDocDate(parts.date));
    return bits.join(' - ');
}
/**
 * Replace the date, and optionally the provider, in a title built by
 * `buildTitle` ("Provider - Specialty - Type - Date"). The sort review's
 * corrections use it so a corrected document keeps the title convention the
 * schedule of documents relies on.
 */
export function retitle(doc, change) {
    const title = doc.title.trim();
    const cut = title.lastIndexOf(' - ');
    const endsInMarker = title.endsWith(DATE_TO_CHECK);
    let head = cut === -1 ? (endsInMarker ? '' : title) : title.slice(0, cut);
    if (cut === -1 && !endsInMarker && change.date === undefined)
        head = title;
    if (change.provider !== undefined) {
        const provider = change.provider.trim();
        if (doc.provider && head.startsWith(doc.provider))
            head = `${provider}${head.slice(doc.provider.length)}`;
        else
            head = head ? `${provider} - ${head}` : provider;
    }
    const date = change.date === undefined ? doc.date : change.date;
    return head ? `${head} - ${formatDocDate(date)}` : formatDocDate(date);
}
function dateValue(d) {
    if (!d)
        return -1;
    return d.year * 10000 + d.month * 100 + (d.day ?? 0);
}
/** Order the sorted casefile the way the output PDF and its bookmark tree read. */
export function organise(documents) {
    const out = [];
    for (const section of SECTION_ORDER) {
        const inSection = documents.filter((d) => d.section === section);
        if (inSection.length === 0)
            continue;
        if (PROVIDER_GROUPED.has(section)) {
            const byProvider = new Map();
            for (const doc of inSection) {
                const key = doc.provider?.trim() || 'Unattributed';
                const list = byProvider.get(key) ?? [];
                list.push(doc);
                byProvider.set(key, list);
            }
            // Providers ordered by their most recent document, so the reader meets
            // the most current opinion first.
            const providers = [...byProvider.entries()].sort((a, b) => {
                const aMax = Math.max(...a[1].map((d) => dateValue(d.date)));
                const bMax = Math.max(...b[1].map((d) => dateValue(d.date)));
                return bMax - aMax || a[0].localeCompare(b[0]);
            });
            for (const [heading, docs] of providers) {
                out.push({
                    section,
                    heading,
                    documents: docs.sort((a, b) => dateValue(b.date) - dateValue(a.date)),
                });
            }
            continue;
        }
        if (YEAR_GROUPED.has(section)) {
            const byYear = new Map();
            for (const doc of inSection) {
                const key = doc.date ? String(doc.date.year) : 'Undated';
                const list = byYear.get(key) ?? [];
                list.push(doc);
                byYear.set(key, list);
            }
            const years = [...byYear.entries()].sort((a, b) => b[0].localeCompare(a[0]));
            for (const [heading, docs] of years) {
                out.push({
                    section,
                    heading,
                    documents: docs.sort((a, b) => dateValue(b.date) - dateValue(a.date)),
                });
            }
            continue;
        }
        out.push({
            section,
            heading: null,
            documents: inSection.sort((a, b) => dateValue(b.date) - dateValue(a.date)),
        });
    }
    return out;
}
export function duplicatesTitle(count) {
    return `Duplicates (${count})`;
}
/**
 * Turn a sorted casefile into the report's Schedule of Documents.
 *
 * The schedule lists oldest to newest under each heading, which is the opposite
 * of the bookmark tree. Beam's IME template says so explicitly: "all headings
 * list in chronological order oldest to newest under each heading".
 */
const SCHEDULE_SECTION_KEY = {
    'Referral Letter': 'schedule_referral_letter',
    Imaging: 'schedule_imaging',
    Specialist: 'schedule_specialists',
    "Workers' Compensation Certificate": 'schedule_work_certificates',
    'Rehabilitation - Physical': 'schedule_physiotherapy',
    'Rehabilitation - Vocational': 'schedule_rehabilitation',
    'Clinical Notes': 'schedule_clinical_notes',
    'Claim Form': 'schedule_other',
    Other: 'schedule_other',
};
export function scheduleOfDocuments(casefile) {
    const schedule = {};
    const live = casefile.documents.filter((d) => !d.duplicateOf && d.section !== 'Duplicates');
    for (const doc of live) {
        const key = SCHEDULE_SECTION_KEY[doc.section];
        if (!key)
            continue;
        (schedule[key] ??= []).push(doc.title);
    }
    for (const key of Object.keys(schedule)) {
        const docs = live.filter((d) => SCHEDULE_SECTION_KEY[d.section] === key);
        schedule[key] = docs
            .slice()
            .sort((a, b) => dateValue(a.date) - dateValue(b.date))
            .map((d) => d.title);
    }
    return schedule;
}
/**
 * Duplicates do not count toward the page count and are billed by the number of
 * actual pages. GIO's service definition includes reading up to 250 pages, so
 * this number is directly chargeable and must be right.
 */
export function pageAccounting(documents, totalPages) {
    const duplicatePages = documents
        .filter((d) => d.duplicateOf || d.section === 'Duplicates')
        .reduce((sum, d) => sum + (d.endPage - d.startPage + 1), 0);
    return { duplicatePages, billablePages: Math.max(0, totalPages - duplicatePages) };
}
//# sourceMappingURL=casefile.js.map